import type { AuthRuntimeConfig } from '@/config/env';
import { ApiClient, type AccessTokenProvider } from '@/lib/api';

import { PublicCatalogApi } from './catalogApi';
import type {
  FeaturedProductListInput,
  PublicCategoryListInput,
  PublicCatalogService,
  PublicProductListInput,
} from './types';

const noToken: AccessTokenProvider = { getAccessToken: async () => null };

export function createPublicCatalogService(config: AuthRuntimeConfig): PublicCatalogService {
  return config.catalogDataSource === 'mock'
    ? new LazyMockPublicCatalogService()
    : new PublicCatalogApi(new ApiClient(config.apiBaseUrl, noToken));
}

export class LazyMockPublicCatalogService implements PublicCatalogService {
  private service?: Promise<PublicCatalogService>;
  private load() {
    return (this.service ??= import('./mockCatalogService').then(
      ({ MockPublicCatalogService }) => new MockPublicCatalogService(),
    ));
  }
  async listCategories(input?: PublicCategoryListInput, signal?: AbortSignal) {
    return (await this.load()).listCategories(input, signal);
  }
  async getCategory(slug: string, signal?: AbortSignal) {
    return (await this.load()).getCategory(slug, signal);
  }
  async listProducts(input?: PublicProductListInput, signal?: AbortSignal) {
    return (await this.load()).listProducts(input, signal);
  }
  async listCategoryProducts(
    slug: string,
    input?: Omit<PublicProductListInput, 'category'>,
    signal?: AbortSignal,
  ) {
    return (await this.load()).listCategoryProducts(slug, input, signal);
  }
  async getProduct(slug: string, signal?: AbortSignal) {
    return (await this.load()).getProduct(slug, signal);
  }
  async listFeaturedProducts(input?: FeaturedProductListInput, signal?: AbortSignal) {
    return (await this.load()).listFeaturedProducts(input, signal);
  }
}
